import React from 'react';
import { motion } from 'framer-motion';
import { masteringPresets } from '../lib/masteringPresets';
import { Music2, AudioWaveform as Waveform, Sliders, Layers, Brain } from 'lucide-react';
import { MasteringPreset } from '../types';
import { InfoTooltip } from './InfoTooltip';

interface PresetSelectorProps {
  selectedPreset: string;
  onPresetChange: (presetId: string) => void;
}

const presetIcons = {
  'abbey-road': Music2,
  'hip-hop-punch': Waveform,
  'jazz-finesse': Music2,
  'cinematic-epic': Layers,
  'lofi-warmth': Sliders,
  'future-bass': Waveform,
  'classical-hall': Music2,
  'rock-aggression': Waveform,
  'ambient-space': Layers,
  'vocal-presence': Music2
} as const;

const presetInfo = {
  'abbey-road': {
    title: 'Abbey Road Style',
    description: 'Classic British console sound with natural compression and subtle tape saturation.',
    usage: 'Perfect for rock, jazz, and acoustic recordings seeking vintage warmth.'
  },
  'hip-hop-punch': {
    title: 'Hip-Hop Punch',
    description: 'Heavy-hitting drums with deep bass and crisp highs.',
    usage: 'Ideal for hip-hop, trap, and bass-heavy genres.'
  },
  'jazz-finesse': {
    title: 'Jazz Finesse',
    description: 'Subtle enhancement with preserved dynamics and natural sound.',
    usage: 'Perfect for jazz, acoustic, and classical recordings.'
  },
  'cinematic-epic': {
    title: 'Cinematic Epic',
    description: 'Wide, dramatic sound with enhanced dynamics and spatial processing.',
    usage: 'Great for film scores, orchestral music, and epic compositions.'
  },
  'lofi-warmth': {
    title: 'Lo-Fi Warmth',
    description: 'Vintage character with subtle tape saturation and rolled-off highs.',
    usage: 'Perfect for lo-fi hip hop, chill beats, and vintage vibes.'
  },
  'future-bass': {
    title: 'Future Bass',
    description: 'Modern, wide sound with heavy sidechain and bright harmonics.',
    usage: 'Ideal for EDM, future bass, and modern electronic music.'
  },
  'classical-hall': {
    title: 'Classical Hall',
    description: 'Natural sound with preserved dynamics and subtle enhancement.',
    usage: 'Perfect for classical recordings and acoustic performances.'
  },
  'rock-aggression': {
    title: 'Rock Aggression',
    description: 'Aggressive sound with enhanced mids and punchy dynamics.',
    usage: 'Great for rock, metal, and high-energy genres.'
  },
  'ambient-space': {
    title: 'Ambient Space',
    description: 'Wide, ethereal sound with subtle processing and enhanced depth.',
    usage: 'Perfect for ambient, atmospheric, and spatial music.'
  },
  'vocal-presence': {
    title: 'Vocal Presence',
    description: 'Enhanced vocal clarity and presence with controlled dynamics.',
    usage: 'Ideal for vocal-focused tracks and acoustic performances.'
  }
} as const;

export function PresetSelector({ selectedPreset, onPresetChange }: PresetSelectorProps) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Brain className="w-6 h-6 text-blue-400" />
        <h3 className="text-lg font-semibold text-blue-100">AI Mastering Style</h3>
        <InfoTooltip
          title="AI Mastering Presets"
          description="Choose from professional mastering presets designed for different genres and styles."
          usage="Select a preset that matches your music genre for optimal results."
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {masteringPresets.map((preset) => {
          const Icon = presetIcons[preset.id as keyof typeof presetIcons] || Music2;
          const info = presetInfo[preset.id as keyof typeof presetInfo];
          
          return (
            <motion.button
              key={preset.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onPresetChange(preset.id)}
              className={`
                glass-panel p-6 text-left transition-all duration-300
                ${selectedPreset === preset.id
                  ? 'border-2 border-blue-500/50 neon-glow'
                  : 'border border-white/10 hover:border-blue-500/30'
                }
              `}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`
                  p-2 rounded-lg bg-gray-800/50
                  ${selectedPreset === preset.id ? 'text-blue-400' : 'text-gray-400'}
                `}>
                  <Icon className="w-5 h-5" />
                </div>
                <h4 className="font-medium text-blue-100">{preset.name}</h4>
                {info && (
                  <InfoTooltip
                    title={info.title}
                    description={info.description}
                    usage={info.usage}
                    position="right"
                  />
                )}
              </div>
              <p className="text-sm text-gray-400">{preset.description}</p>
              
              {selectedPreset === preset.id && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-4 pt-4 border-t border-blue-500/20"
                >
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="p-2 rounded-lg bg-blue-900/20 text-blue-300">
                      <span className="block text-blue-200 mb-1">Compression</span>
                      {preset.parameters.compression.ratio}:1
                    </div>
                    <div className="p-2 rounded-lg bg-purple-900/20 text-purple-300">
                      <span className="block text-purple-200 mb-1">Drive</span>
                      {preset.parameters.saturation.drive}
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}